import { createEffect, createEvent, createStore, sample } from "effector";
import { ModelContract, ModuleDescription } from "@features/shared/module-description";
import { StudentApi } from "@features/shared/api/course-data.api";
import { IMetadataResponse, ISetMetadataRequest, IDeleteMetadataRequest } from "@features/shared/entities/metadata";
import { appMounted } from "@features/app/model";

const parseNumber = (value?: string) => {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
};

// Events
export const metadataLoaded = createEvent<IMetadataResponse>();
export const setMetadataRequested = createEvent<ISetMetadataRequest>();
export const setMetadataAnonRequested = createEvent<{ email: string; data: ISetMetadataRequest }>();
export const deleteMetadataRequested = createEvent<IDeleteMetadataRequest>();
export const refreshMetadataRequested = createEvent();
export const trustpilotSkipped = createEvent();
export const trustpilotOpened = createEvent();

// Stores
export const $metadata = createStore<Record<string, string>>({});
export const $metadataLoading = createStore(false);
export const $metadataError = createStore<string | null>(null);
export const $metadataInitialized = createStore(false);
export const $trustpilotSkippedCount = $metadata.map((metadata) => parseNumber(metadata.trustpilot_skipped_count));
export const $trustpilotOpened = $metadata.map((metadata) => metadata.trustpilot_opened === "true");

// Effects
export const getMetadataFx = createEffect(async (): Promise<IMetadataResponse> => {
    return await StudentApi.getMetadata();
});

export const setMetadataFx = createEffect(async (data: ISetMetadataRequest): Promise<IMetadataResponse> => {
    return await StudentApi.setMetadata(data);
});

export const setMetadataAnonFx = createEffect(
    async ({ email, data }: { email: string; data: ISetMetadataRequest }): Promise<IMetadataResponse> => {
        return await StudentApi.setMetadataAnon(email, data);
    }
);

export const deleteMetadataFx = createEffect(async (data: IDeleteMetadataRequest): Promise<IMetadataResponse> => {
    return await StudentApi.deleteMetadata(data);
});

// Sync localStorage with metadata
export const syncLocalStorageWithMetadataFx = createEffect(() => {
    if (typeof window === "undefined") return;

    const productOnboardingFinished = localStorage.getItem("productOnboardingFinished");
    const currentMetadata = $metadata.getState();

    if (productOnboardingFinished && !currentMetadata.productOnboardingFinished) {
        setMetadataRequested({ key: "productOnboardingFinished", value: productOnboardingFinished });
    }
});

// Store updates
$metadata
    .on(metadataLoaded, (_, response) => response.metadata)
    .on(setMetadataFx.doneData, (_, response) => response.metadata)
    .on(setMetadataAnonFx.doneData, (_, response) => response.metadata)
    .on(deleteMetadataFx.doneData, (_, response) => response.metadata);

$metadataLoading
    .on(getMetadataFx.pending, (_, pending) => pending)
    .on(setMetadataFx.pending, (_, pending) => pending)
    .on(setMetadataAnonFx.pending, (_, pending) => pending)
    .on(deleteMetadataFx.pending, (_, pending) => pending);

$metadataError
    .on(getMetadataFx.failData, (_, error) => error.message)
    .on(setMetadataFx.failData, (_, error) => error.message)
    .on(setMetadataAnonFx.failData, (_, error) => error.message)
    .on(deleteMetadataFx.failData, (_, error) => error.message)
    .reset(metadataLoaded, setMetadataRequested, deleteMetadataRequested, refreshMetadataRequested);

// Flip initialized when an attempt to interact with metadata happens or when it's loaded
$metadataInitialized
    .on(getMetadataFx, () => true)
    .on(setMetadataFx, () => true)
    .on(setMetadataAnonFx, () => true)
    .on(deleteMetadataFx, () => true)
    .on(metadataLoaded, () => true);

// Samples
sample({
    clock: setMetadataRequested,
    target: setMetadataFx
});

sample({
    clock: setMetadataAnonRequested,
    fn: ({ email, data }) => ({ email, data }),
    target: setMetadataAnonFx
});

sample({
    clock: deleteMetadataRequested,
    target: deleteMetadataFx
});

sample({
    source: $trustpilotSkippedCount,
    clock: trustpilotSkipped,
    fn: (count) => ({ key: "trustpilot_skipped_count", value: String(count + 1) }),
    target: setMetadataRequested
});

sample({
    clock: trustpilotOpened,
    fn: () => ({ key: "trustpilot_opened", value: "true" }),
    target: setMetadataRequested
});

sample({
    clock: refreshMetadataRequested,
    target: getMetadataFx
});

sample({
    clock: getMetadataFx.doneData,
    target: metadataLoaded
});

// After metadata is loaded (e.g., right after login), sync localStorage flags into metadata
sample({
    clock: metadataLoaded,
    target: syncLocalStorageWithMetadataFx
});

// Sync localStorage with metadata on app mount
sample({
    clock: appMounted,
    target: syncLocalStorageWithMetadataFx
});

// Contract
const contract: ModelContract<
    {
        $metadata: typeof $metadata;
        $metadataLoading: typeof $metadataLoading;
        $metadataError: typeof $metadataError;
        $metadataInitialized: typeof $metadataInitialized;
        $trustpilotSkippedCount: typeof $trustpilotSkippedCount;
        $trustpilotOpened: typeof $trustpilotOpened;
    },
    {
        metadataLoaded: typeof metadataLoaded;
        setMetadataRequested: typeof setMetadataRequested;
        setMetadataAnonRequested: typeof setMetadataAnonRequested;
        deleteMetadataRequested: typeof deleteMetadataRequested;
        refreshMetadataRequested: typeof refreshMetadataRequested;
        trustpilotSkipped: typeof trustpilotSkipped;
        trustpilotOpened: typeof trustpilotOpened;
    },
    {
        getMetadataFx: typeof getMetadataFx;
        setMetadataFx: typeof setMetadataFx;
        setMetadataAnonFx: typeof setMetadataAnonFx;
        deleteMetadataFx: typeof deleteMetadataFx;
        syncLocalStorageWithMetadataFx: typeof syncLocalStorageWithMetadataFx;
    },
    {}
> = {
    stores: {
        $metadata,
        $metadataLoading,
        $metadataError,
        $metadataInitialized,
        $trustpilotSkippedCount,
        $trustpilotOpened
    },
    events: {
        metadataLoaded,
        setMetadataRequested,
        setMetadataAnonRequested,
        deleteMetadataRequested,
        refreshMetadataRequested,
        trustpilotSkipped,
        trustpilotOpened
    },
    effects: {
        getMetadataFx,
        setMetadataFx,
        setMetadataAnonFx,
        deleteMetadataFx,
        syncLocalStorageWithMetadataFx
    },
    __overridesForTests: {}
};

export type IMetadataContract = typeof contract;

export class MetadataModule extends ModuleDescription<IMetadataContract> {
    getDefaultContract() {
        return contract;
    }
}
